import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { LoadingState } from './ui/LoadingStates';

/**
 * Protected route component that checks user authentication and role permissions
 */
const RoleProtectedRoute = ({
  children,
  allowedRoles = [],
  requireAuth = true,
  redirectTo = '/login',
  fallback = null,
  loadingComponent = null
}) => {
  const authContext = useAuth();
  const { user = null, loading = true } = authContext || {};
  const { hasAnyRole, userRole } = useRoleAccess();
  const location = useLocation();

  // Show loading state while checking authentication
  if (loading) {
    return loadingComponent || <LoadingState message="Checking authentication..." />;
  }

  // Redirect to login if authentication is required but user is not logged in
  if (requireAuth && !user) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check role permissions if roles are specified
  if (allowedRoles?.length > 0 && !hasAnyRole(allowedRoles)) {
    // Show fallback or redirect to unauthorized page
    return fallback || (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">
            You don't have permission to access this page.
          </p>
          <p className="text-sm text-gray-500">
            Your role: <span className="font-semibold">{userRole}</span>
          </p>
        </div>
      </div>
    );
  }

  return children;
};

export default RoleProtectedRoute;